import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';



import './FriendSuggestions.css';

import AvatarPreview from '../avatar/AvatarPreview';
import { defaultAvatar } from '../avatar/avatarConfig';

const apiUrl =
  process.env.REACT_APP_API_URL;

const MAX_SUGGESTIONS = 3;

const FriendSuggestions = ({
  onSeeAll,
  onOpenUser,
}) => {


  const [suggestions, setSuggestions] =
    useState([]);

  const [loadingIds, setLoadingIds] =
    useState([]);

  const [isLoading, setIsLoading] =
    useState(true);

  // ==========================================
  // CERE SUGESTII DE LA SERVER
  // ==========================================

  const requestSuggestions =
    useCallback(async () => {
      const response = await fetch(
        `${apiUrl}/user/friend-suggestions`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(
          'Nu s-au putut incarca sugestiile.'
        );
      }

      const data =
        await response.json();

      return Array.isArray(data)
        ? data
        : [];
    }, []);

  // ==========================================
  // INCARCARE INITIALA
  // ==========================================

  useEffect(() => {
    const loadSuggestions = async () => {
      setIsLoading(true);

      try {
        const data =
          await requestSuggestions();

        setSuggestions(
          data.slice(
            0,
            MAX_SUGGESTIONS
          )
        );

      } catch (error) {
        console.error(
          'Eroare sugestii prieteni:',
          error
        );

        setSuggestions([]);

      } finally {
        setIsLoading(false);
      }
    };

    loadSuggestions();
  }, [requestSuggestions]);

  // ==========================================
  // DESCHIDE PROFILUL USERULUI
  // ==========================================

const handleOpenProfile = (
  userId
) => {
  if (!userId) {
    return;
  }

  if (onOpenUser) {
    onOpenUser(userId);
  }
};

  // ==========================================
  // INLOCUIESTE O SUGESTIE
  // ==========================================

  const replaceSuggestion = async (
    removedUserId
  ) => {
    // Eliminam imediat cardul din interfata.
    setSuggestions((current) =>
      current.filter(
        (user) =>
          user.user_id !==
          removedUserId
      )
    );

    try {
      const freshSuggestions =
        await requestSuggestions();

      setSuggestions((current) => {
        const currentIds =
          new Set(
            current.map(
              (user) =>
                user.user_id
            )
          );

        // Nu reintroducem utilizatorul
        // care tocmai a fost eliminat.
        currentIds.add(
          removedUserId
        );

        const nextSuggestion =
          freshSuggestions.find(
            (user) =>
              !currentIds.has(
                user.user_id
              )
          );

        if (!nextSuggestion) {
          return current;
        }

        return [
          ...current,
          nextSuggestion,
        ].slice(
          0,
          MAX_SUGGESTIONS
        );
      });

    } catch (error) {
      console.error(
        'Eroare la inlocuirea sugestiei:',
        error
      );
    }
  };

  // ==========================================
  // ELIMINA SUGESTIE CU X
  // ==========================================

  const handleDismiss = async (
    userId
  ) => {
    if (
      loadingIds.includes(userId)
    ) {
      return;
    }

    setLoadingIds((current) => [
      ...current,
      userId,
    ]);

    try {
      await replaceSuggestion(
        userId
      );

    } finally {
      setLoadingIds((current) =>
        current.filter(
          (id) =>
            id !== userId
        )
      );
    }
  };

  // ==========================================
  // URMARESTE USER
  // ==========================================

  const handleFollow = async (
    userId
  ) => {
    if (
      loadingIds.includes(userId)
    ) {
      return;
    }

    setLoadingIds((current) => [
      ...current,
      userId,
    ]);

    try {
      const response = await fetch(
        `${apiUrl}/user/follow/${userId}`,
        {
          method: 'POST',
          credentials: 'include',

          headers: {
            'Content-Type':
              'application/json',
          },
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data?.message ||
          'Nu s-a putut urmari utilizatorul.'
        );
      }

      // Anuntam celelalte componente
      // ca lista de urmariti s-a modificat.
      window.dispatchEvent(
        new CustomEvent(
          'followUpdated',
          {
            detail: {
              action: 'follow',
              userId,
            },
          }
        )
      );

      // Scoatem utilizatorul urmarit
      // si afisam urmatoarea sugestie.
      await replaceSuggestion(
        userId
      );

    } catch (error) {
      console.error(
        'Eroare la urmarirea utilizatorului:',
        error
      );

    } finally {
      setLoadingIds((current) =>
        current.filter(
          (id) =>
            id !== userId
        )
      );
    }
  };

  // ==========================================
  // VEZI TOT
  // ==========================================

  const handleSeeAll = () => {
    if (onSeeAll) {
      onSeeAll();
    }
  };

  // ==========================================
  // LOADING
  // ==========================================

  if (isLoading) {
    return null;
  }

  // ==========================================
  // FARA SUGESTII
  // ==========================================

  if (
    suggestions.length === 0
  ) {
    return null;
  }

  // ==========================================
  // RETURN
  // ==========================================

  return (
    <section className="friend-suggestions">

      <div className="friend-suggestions-header">

        <h2>
          Sugestii de prieteni
        </h2>

        <button
          type="button"
          className="friend-suggestions-see-all"
          onClick={handleSeeAll}
        >
          VEZI TOT
        </button>

      </div>

      <div className="friend-suggestions-list">

        {suggestions.map(
          (user) => {
            const isLoadingUser =
              loadingIds.includes(
                user.user_id
              );

            const userAvatar = {
              ...defaultAvatar,
              ...(user.avatar_config || {}),
            };

            return (
              <article
                className="friend-suggestion-card"
                key={user.user_id}
              >

                {/* ==================================
                    ELIMINA SUGESTIA
                ================================== */}

                <button
                  type="button"
                  className="friend-suggestion-dismiss"
                  disabled={
                    isLoadingUser
                  }
                  onClick={() =>
                    handleDismiss(
                      user.user_id
                    )
                  }
                  aria-label={
                    `Elimina sugestia ${
                      user.display_name ||
                      'Utilizator'
                    }`
                  }
                >
                  ×
                </button>

                {/* ==================================
                    PROFIL USER
                ================================== */}

                <button
                  type="button"
                  className="friend-suggestion-profile"
                  onClick={() =>
                    handleOpenProfile(
                      user.user_id
                    )
                  }
                  aria-label={
                    `Deschide profilul ${
                      user.display_name ||
                      'utilizatorului'
                    }`
                  }
                >

                  <div className="friend-suggestion-avatar">

                    <AvatarPreview
                      avatar={
                        userAvatar
                      }
                      className="avatar-preview-friend-suggestion"
                    />

                  </div>

                  <div className="friend-suggestion-name">
                    {user.display_name ||
                      'Utilizator'}
                  </div>

                  <div className="friend-suggestion-username">
                    @{user.display_name ||
                      'utilizator'}
                  </div>

                </button>

                {/* ==================================
                    FOLLOW
                ================================== */}

                <button
                  type="button"
                  className="friend-suggestion-follow"
                  disabled={
                    isLoadingUser
                  }
                  onClick={() =>
                    handleFollow(
                      user.user_id
                    )
                  }
                >

                  {isLoadingUser
                    ? 'SE INCARCA...'
                    : 'URMARESTE'}

                </button>

              </article>
            );
          }
        )}

      </div>

    </section>
  );
};

export default FriendSuggestions;